import { useState, useEffect, useRef, useCallback } from 'react';
import { X, Heart, MessageCircle, Send, Bookmark, Grid3X3, MoreHorizontal, ChevronLeft, ChevronRight, Play } from 'lucide-react';
import './PhonePreview.css';

const AVATAR_URL = '/mock-content/curchods-avatar.jpg';
const avatarStyle = { background: `url(${AVATAR_URL}) center/cover` };

const MOCK_POSTS = [
  { id: 1, images: ['/mock-content/1.png'], caption: 'Just Listed! Stunning property now available. Contact us for viewings.', likes: 47, day: 'Monday', cropTop: true },
  { id: 2, images: ['/mock-content/2.png'], caption: 'Beautiful interiors that speak for themselves. Book your private tour today.', likes: 62, day: 'Monday' },
  { id: 3, images: ['/mock-content/3.png'], caption: 'Location, location, location. Perfectly situated for modern living.', likes: 38, day: 'Tuesday' },
  { id: 4, images: ['/mock-content/4.png'], caption: 'Dream kitchen goals. This could be yours!', likes: 85, day: 'Wednesday' },
  { id: 5, images: ['/mock-content/farley-hill.png'], caption: 'Normandy, Guildford. A beautifully presented detached family home in a sought-after location.', likes: 71, day: 'Thursday', cropTop: true },
  { id: 6, images: ['/mock-content/new-listing.png'], caption: 'NEW LISTING! Only a week on the market. Don\'t miss this stunning property.', likes: 93, day: 'Thursday' },
  { id: 7, images: ['/mock-content/8.png', '/mock-content/9.png', '/mock-content/10.png'], caption: 'Swipe through this stunning property tour! Every room tells a story.', likes: 103, day: 'Friday', isCarousel: true },
];

function PostDetail({ post, onClose, onNext, onPrev, hasNext, hasPrev }) {
  const [carouselIdx, setCarouselIdx] = useState(0);
  const [liked, setLiked] = useState(false);
  const [saved, setSaved] = useState(false);
  const [doubleTapHeart, setDoubleTapHeart] = useState(false);
  const lastTapRef = useRef(0);
  const touchStartX = useRef(null);

  const handleImageDoubleTap = () => {
    const now = Date.now();
    if (now - lastTapRef.current < 300) {
      setLiked(true);
      setDoubleTapHeart(true);
      setTimeout(() => setDoubleTapHeart(false), 900);
    }
    lastTapRef.current = now;
  };

  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0 && carouselIdx < post.images.length - 1) setCarouselIdx(i => i + 1);
      if (diff < 0 && carouselIdx > 0) setCarouselIdx(i => i - 1);
    }
    touchStartX.current = null;
  };

  return (
    <div className="ig-post-detail" data-testid="ig-post-detail">
      <div className="ig-post-detail-header">
        <button className="ig-post-back" onClick={onClose} data-testid="ig-post-back">
          <ChevronLeft size={24} />
        </button>
        <span className="ig-post-header-title">Posts</span>
        <div style={{ width: 24 }} />
      </div>

      <div className="ig-post-detail-scroll">
        <div className="ig-post-user-row">
          <div className="ig-avatar-small" style={avatarStyle} />
          <span className="ig-post-username">curchods_branch</span>
          <MoreHorizontal size={16} className="ig-post-more" />
        </div>

        <div
          className="ig-post-image-wrap"
          onClick={handleImageDoubleTap}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <img src={post.images[carouselIdx]} alt="" className="ig-post-image" />
          {doubleTapHeart && (
            <div className="ig-double-tap-heart">
              <Heart size={80} fill="white" color="white" />
            </div>
          )}
          {post.images.length > 1 && (
            <>
              <div className="ig-carousel-dots-overlay">
                {post.images.map((_, i) => (
                  <div key={i} className={`ig-carousel-dot ${i === carouselIdx ? 'active' : ''}`} />
                ))}
              </div>
              {carouselIdx > 0 && (
                <button className="ig-carousel-nav ig-carousel-nav-left" onClick={(e) => { e.stopPropagation(); setCarouselIdx(i => i - 1); }}>
                  <ChevronLeft size={18} />
                </button>
              )}
              {carouselIdx < post.images.length - 1 && (
                <button className="ig-carousel-nav ig-carousel-nav-right" onClick={(e) => { e.stopPropagation(); setCarouselIdx(i => i + 1); }}>
                  <ChevronRight size={18} />
                </button>
              )}
              <div className="ig-carousel-counter">{carouselIdx + 1}/{post.images.length}</div>
            </>
          )}
        </div>

        <div className="ig-post-actions">
          <div className="ig-post-actions-left">
            <button className={`ig-action-btn ${liked ? 'ig-liked' : ''}`} onClick={() => setLiked(!liked)}>
              <Heart size={22} fill={liked ? '#ed4956' : 'none'} color={liked ? '#ed4956' : 'white'} />
            </button>
            <button className="ig-action-btn"><MessageCircle size={22} /></button>
            <button className="ig-action-btn"><Send size={22} /></button>
          </div>
          <button className={`ig-action-btn ${saved ? 'ig-saved' : ''}`} onClick={() => setSaved(!saved)}>
            <Bookmark size={22} fill={saved ? 'white' : 'none'} />
          </button>
        </div>

        <div className="ig-post-likes">{liked ? post.likes + 1 : post.likes} likes</div>
        <div className="ig-post-caption">
          <span className="ig-caption-user">curchods_branch</span> {post.caption}
        </div>
        <div className="ig-post-time">Posted {post.day}</div>
      </div>
    </div>
  );
}

export default function PhonePreview({ onClose }) {
  const [loaded, setLoaded] = useState(false);
  const [contentReady, setContentReady] = useState(false);
  const [view, setView] = useState('grid');
  const [selectedPost, setSelectedPost] = useState(null);

  useEffect(() => {
    requestAnimationFrame(() => setLoaded(true));
    // Show loading shimmer, then reveal content
    const timer = setTimeout(() => setContentReady(true), 1800);
    return () => { setLoaded(false); clearTimeout(timer); };
  }, []);

  const handleClose = useCallback(() => {
    setLoaded(false);
    setTimeout(onClose, 500);
  }, [onClose]);

  const selectedIdx = selectedPost ? MOCK_POSTS.findIndex(p => p.id === selectedPost.id) : -1;

  return (
    <div className={`phone-overlay ${loaded ? 'phone-overlay-visible' : ''}`} onClick={handleClose} data-testid="phone-preview-overlay">
      <div className={`phone-frame ${loaded ? 'phone-frame-visible' : ''}`} onClick={e => e.stopPropagation()}>
        {/* iPhone notch */}
        <div className="phone-notch">
          <div className="phone-notch-pill" />
        </div>

        {/* Screen content */}
        <div className="phone-screen">
          {!contentReady ? (
            <div className="ig-loader">
              <div className="ig-loader-logo">
                <img src="/mock-content/curchods-avatar.jpg" alt="" className="ig-loader-avatar" />
              </div>
              <div className="ig-loader-shimmer">
                <div className="ig-shimmer-row">
                  <div className="ig-shimmer-circle" />
                  <div className="ig-shimmer-lines">
                    <div className="ig-shimmer-line ig-shimmer-line-long" />
                    <div className="ig-shimmer-line ig-shimmer-line-short" />
                  </div>
                </div>
                <div className="ig-shimmer-grid">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="ig-shimmer-tile" style={{ animationDelay: `${i * 0.1}s` }} />
                  ))}
                </div>
              </div>
              <div className="ig-loader-text">Loading your week...</div>
            </div>
          ) : selectedPost ? (
            <PostDetail
              post={selectedPost}
              onClose={() => setSelectedPost(null)}
              onNext={selectedIdx < MOCK_POSTS.length - 1 ? () => setSelectedPost(MOCK_POSTS[selectedIdx + 1]) : null}
              onPrev={selectedIdx > 0 ? () => setSelectedPost(MOCK_POSTS[selectedIdx - 1]) : null}
              hasNext={selectedIdx < MOCK_POSTS.length - 1}
              hasPrev={selectedIdx > 0}
            />
          ) : (
            <>
              {/* Instagram Profile Header */}
              <div className="ig-header">
                <div className="ig-header-top">
                  <span className="ig-username">curchods_branch</span>
                  <button className="ig-close-btn" onClick={handleClose} data-testid="phone-close-btn">
                    <X size={18} />
                  </button>
                </div>
              </div>

              <div className="ig-profile-scroll">
                <div className="ig-profile-info">
                  <div className="ig-avatar-ring">
                    <div className="ig-avatar" style={avatarStyle} />
                  </div>
                  <div className="ig-stats">
                    <div className="ig-stat"><strong>{MOCK_POSTS.length}</strong><span>posts</span></div>
                    <div className="ig-stat"><strong>2.4K</strong><span>followers</span></div>
                    <div className="ig-stat"><strong>186</strong><span>following</span></div>
                  </div>
                </div>

                <div className="ig-bio">
                  <div className="ig-bio-name">Curchods - Branch Name</div>
                  <div className="ig-bio-text">Award-winning estate agents across Surrey</div>
                  <div className="ig-bio-link">curchods.com</div>
                </div>

                <div className="ig-action-row">
                  <button className="ig-follow-btn">Follow</button>
                  <button className="ig-message-btn">Message</button>
                </div>

                {/* Story highlights placeholder */}
                <div className="ig-highlights">
                  {['New', 'Sold', 'Reviews', 'Team'].map(label => (
                    <div key={label} className="ig-highlight">
                      <div className="ig-highlight-circle" />
                      <span className="ig-highlight-label">{label}</span>
                    </div>
                  ))}
                </div>

                {/* Grid/Feed toggle */}
                <div className="ig-tab-bar">
                  <button
                    className={`ig-tab ${view === 'grid' ? 'ig-tab-active' : ''}`}
                    onClick={() => setView('grid')}
                    data-testid="ig-grid-tab"
                  >
                    <Grid3X3 size={20} />
                  </button>
                  <button
                    className={`ig-tab ${view === 'feed' ? 'ig-tab-active' : ''}`}
                    onClick={() => setView('feed')}
                    data-testid="ig-feed-tab"
                  >
                    <Play size={20} />
                  </button>
                </div>

                {/* Grid View */}
                {view === 'grid' && (
                  <div className="ig-grid">
                    {MOCK_POSTS.map((post, idx) => (
                      <button
                        key={post.id}
                        className="ig-grid-item"
                        style={{ animationDelay: `${0.6 + idx * 0.08}s` }}
                        onClick={() => setSelectedPost(post)}
                        data-testid={`ig-grid-item-${idx}`}
                      >
                        <img src={post.images[0]} alt="" className={`ig-grid-img ${post.cropTop ? 'ig-crop-top' : ''}`} loading="lazy" />
                        {post.isCarousel && (
                          <div className="ig-grid-carousel-badge">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="white"><path d="M4 6h12v12H4V6zm2 2v8h8V8H6zm10-4h4v16H8v-2h6V4h2z"/></svg>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}

                {/* Feed/Reels View */}
                {view === 'feed' && (
                  <div className="ig-reels-empty">
                    <Play size={40} />
                    <span className="ig-reels-empty-title">No Reels Yet</span>
                    <span className="ig-reels-empty-sub">Video content coming soon</span>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* iPhone home indicator */}
        <div className="phone-home-bar">
          <div className="phone-home-pill" />
        </div>
      </div>
    </div>
  );
}
